import { forwardRef } from 'react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
} from 'recharts';

const GRADE_META = {
  'A': { label: 'Excellent', color: '#10b981', bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
  'B': { label: 'Good',      color: '#3b82f6', bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
  'C': { label: 'Satisfactory', color: '#f59e0b', bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  'D': { label: 'Developing', color: '#f97316', bg: '#ffedd5', border: '#fdba74', text: '#7c2d12' },
};

const GRADE_VALUES = { A: 4, B: 3, C: 2, D: 1 };

// SVG-based centering for pixel-perfect text alignment in html2canvas
const GradeBox = ({ grade, size = 44, fontSize = 20, radius = 10, solid = false }) => {
  const m = GRADE_META[grade] || GRADE_META['C'];
  const borderW = 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
      <rect
        x={borderW / 2}
        y={borderW / 2}
        width={size - borderW}
        height={size - borderW}
        rx={radius}
        ry={radius}
        fill={solid ? m.color : m.bg}
        stroke={solid ? m.color : m.border}
        strokeWidth={borderW}
      />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={fontSize}
        fontWeight={800}
        fill={solid ? '#fff' : m.text}
        fontFamily="sans-serif"
      >
        {grade}
      </text>
    </svg>
  );
};

const GradeBadge = ({ grade }) => <GradeBox grade={grade} size={44} fontSize={20} radius={10} />;

const PrintableReport = forwardRef(({ student, report, radarData, comparisonData, previousReport }, ref) => {
  if (!student || !report) return null;

  const getOverallGrade = () => {
    const s = report.grades.performance;
    const avg = (GRADE_VALUES[s.passing] + GRADE_VALUES[s.shooting] + GRADE_VALUES[s.dribbling] +
      GRADE_VALUES[s.heading] + GRADE_VALUES[s.ball_control] + GRADE_VALUES[s.match_play]) / 6;
    if (avg >= 3.5) return 'A';
    if (avg >= 2.5) return 'B';
    if (avg >= 1.5) return 'C';
    return 'D';
  };

  const overall = getOverallGrade();
  const overallMeta = GRADE_META[overall];

  const skills = [
    { key: 'passing', label: 'Passing' },
    { key: 'shooting', label: 'Shooting' },
    { key: 'dribbling', label: 'Dribbling' },
    { key: 'heading', label: 'Heading' },
    { key: 'ball_control', label: 'Ball Control' },
    { key: 'match_play', label: 'Match Play' },
  ];

  return (
    <div
      ref={ref}
      style={{
        width: 794,
        backgroundColor: '#ffffff',
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        color: '#1e293b',
      }}
    >
      {/* ── Header ── */}
      <div className="print-section" style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
        color: '#fff',
        padding: '36px 48px 28px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
      }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5 }}>
            German Football Academy
          </div>
          <div style={{ fontSize: 14, color: '#94a3b8', marginTop: 4 }}>
            Quarterly Performance Report
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#34d399' }}>
            {report.quarter} {report.year}
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            Generated on {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>

      <div style={{ padding: '32px 48px', display: 'flex', flexDirection: 'column', gap: 28 }}>

        {/* ── Student info + Overall grade ── */}
        <div className="print-section" style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
          <div style={{
            flex: 1, background: '#f8fafc', borderRadius: 16, padding: '20px 24px',
            border: '1px solid #e2e8f0',
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>{student.name}</div>
            <div style={{ display: 'flex', gap: 24, color: '#64748b', fontSize: 13 }}>
              <span>Age: <strong style={{ color: '#1e293b' }}>{student.age}</strong></span>
              <span>Level: <strong style={{ color: '#1e293b' }}>{student.skill_level}</strong></span>
            </div>
          </div>
          <div style={{
            width: 140, background: overallMeta.bg, border: `2px solid ${overallMeta.border}`, borderRadius: 16, padding: '16px 8px',
            textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxSizing: 'border-box',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, color: overallMeta.text }}>
              Overall Grade
            </div>
            <svg width={72} height={72} viewBox="0 0 72 72" style={{ display: 'block' }}>
              <text
                x={36} y={36}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={58}
                fontWeight={900}
                fill={overallMeta.color}
                fontFamily="'Helvetica Neue', Arial, sans-serif"
              >{overall}</text>
            </svg>
            <div style={{ fontSize: 12, fontWeight: 700, color: overallMeta.text }}>{overallMeta.label}</div>
          </div>
        </div>

        {/* ── Radar + General Assessment ── */}
        <div className="print-section" style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {/* Radar chart */}
          <div style={{
            flex: '0 0 340px', background: '#f8fafc', borderRadius: 16,
            border: '1px solid #e2e8f0', padding: '16px 12px',
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, textAlign: 'center', color: '#475569' }}>
              Skill Profile
            </div>
            <RadarChart cx={160} cy={140} outerRadius={100} width={320} height={280} data={radarData}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="skill" tick={{ fill: '#64748b', fontSize: 11, fontWeight: 600 }} />
              <PolarRadiusAxis angle={30} domain={[0, 4]} tick={false} axisLine={false} />
              <Radar
                name="Skills"
                dataKey="value"
                stroke="#10b981"
                strokeWidth={2.5}
                fill="#10b981"
                fillOpacity={0.35}
                isAnimationActive={false}
              />
            </RadarChart>
          </div>

          {/* General assessment + legend */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Regularity & Discipline */}
            <div style={{
              background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0', padding: '16px 24px',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                General Assessment
              </div>
              <div style={{ display: 'flex', gap: 24 }}>
                {[
                  { key: 'regularity', label: 'Regularity', grade: report.grades.regularity },
                  { key: 'discipline', label: 'Discipline', grade: report.grades.discipline },
                ].map(({ key, label, grade }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <GradeBadge grade={grade} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{label}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {GRADE_META[grade]?.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Grading legend */}
            <div style={{
              background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0', padding: '16px 24px',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Grading Scale
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {Object.entries(GRADE_META).map(([grade, m]) => (
                  <div key={grade} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <GradeBox grade={grade} size={28} fontSize={13} radius={6} />
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                      {m.label} ({GRADE_VALUES[grade]})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Skills grid ── */}
        <div className="print-section">
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 14, borderBottom: '2px solid #f1f5f9', paddingBottom: 8 }}>
            Football Skills Breakdown
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {skills.map(({ key, label }) => {
              const grade = report.grades.performance[key];
              const m = GRADE_META[grade] || GRADE_META['C'];
              return (
                <div key={key} style={{
                  background: m.bg, borderRadius: 12, padding: '14px 16px',
                  border: `1.5px solid ${m.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{label}</span>
                  <GradeBox grade={grade} size={36} fontSize={18} radius={8} solid />
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Comparison bar chart ── */}
        {previousReport && comparisonData && comparisonData.length > 0 && (
          <div className="print-section" style={{
            background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0', padding: '20px 24px',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
              Quarter Comparison
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
              Current quarter vs previous quarter
            </div>
            <BarChart width={698} height={200} data={comparisonData}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="skill" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 4]} hide />
              <Bar dataKey="previous" name="Previous" fill="#cbd5e1" radius={[4, 4, 0, 0]} barSize={28} isAnimationActive={false} />
              <Bar dataKey="current" name="Current" radius={[4, 4, 0, 0]} barSize={28} isAnimationActive={false}>
                {comparisonData.map((entry, i) => (
                  <Cell key={i} fill={entry.current >= entry.previous ? '#10b981' : '#f59e0b'} />
                ))}
              </Bar>
            </BarChart>
            <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: '#cbd5e1' }} />
                Previous Quarter
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: '#10b981' }} />
                Current Quarter (improved)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: '#f59e0b' }} />
                Current Quarter (declined)
              </div>
            </div>
          </div>
        )}

        {/* ── Remarks ── */}
        <div className="print-section" style={{
          background: '#f0fdf4', borderRadius: 16, padding: '20px 24px',
          border: '1px solid #bbf7d0',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Coach's Remarks
          </div>
          <div style={{ fontSize: 14, color: '#166534', lineHeight: 1.6, fontStyle: 'italic' }}>
            "{report.remarks || 'No specific remarks provided for this quarter.'}"
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="print-section" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderTop: '1px solid #e2e8f0', paddingTop: 12, paddingBottom: 4,
          marginTop: 4,
        }}>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            © 2026 German Football Academy. All rights reserved.
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            Powered by Stadia
          </div>
        </div>
      </div>
    </div>
  );
});

PrintableReport.displayName = 'PrintableReport';
export default PrintableReport;
